import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Col,
  Card,
  Spin,
  Button,
  Input,
  message,
  Row,
  Typography,
  DatePicker,
  Modal,
} from 'antd';
import { DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import InputSearch from 'components/InputSearch';
import Table from 'components/Table';
import Select from 'components/Select';
import LadaNumero from 'components/LadaNumero';
import FormSubmitControls from 'components/FormSubmitControls';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import { onError } from 'utils/handlers';
import { getCatalogoDePadrones, getContentTypesPadrones } from 'api/catalogos';
import { getTiposDeAlertas } from 'api/configuracion/tiposDeAlertas';
import { hasActionPermission, ActionsNames } from 'utils/permission/index';
import { postConsultarCargosPorPadron } from 'api/recaudacion/index';
import { patchAlertas, postGenerarAlerta } from 'api/configuracion';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import padronToString from 'utils/padronesToString';
import { getHelpTexts, gettersPadronesCajaMap } from 'utils/padrones';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import Uploader from 'components/Uploader';
import Moment from 'moment';
import { getUsuario } from 'api/usuarios/usuarios';

const { Title } = Typography;

const catalogsModels = [
  'padron',
  'contenttype',
];

const renderUser = (e) => `${e?.email || ''} - ${e?.first_name || ''} ${e?.last_name || ''} ${e?.second_last_name || ''}`;

// eslint-disable-next-line react/prop-types
const Alertas = ({ permission }) => {
  const { user } = useSelector((state) => state.auth);
  const { padrones, estadosGlobales } = useSelector((state) => state.catalogs);
  const [form] = Form.useForm();
  const [formAlerta] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tipoDePadronSelected, setTipoDePadronSelected] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleForm, setVisibleForm] = useState(false);
  const [dataExterno, setDataExterno] = useState([]);
  const [padron, setPadron] = useState(false);
  const [alertas, setAlertas] = useState([]);
  const [contentTypePadrones, setContentTypePadrones] = useState([]);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [visibleBusquedaAvanzada, setVisibleBusquedaAvanzada] = useState(false);
  const [tiposDeAlertas, setTiposDeAlertas] = useState([]);
  const [visibleCancel, setVisibleCancel] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [formCancel] = Form.useForm();

  useEffect(() => {
    let mounted = true;
    const validatePermission = () => {
      const _hasAllViewPermission = catalogsModels
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) setHasAllViewPermission(_hasAllViewPermission);
    };
    const fetchAll = async () => {
      setLoading(true);
      const [
        _contentTypesPadrones,
        _tiposDeAlertas,
      ] = await Promise.all([
        getContentTypesPadrones(),
        getTiposDeAlertas(),
        getCatalogoDePadrones(),
      ]);
      if (mounted) {
        setContentTypePadrones(_contentTypesPadrones);
        setTiposDeAlertas(_tiposDeAlertas);
        setLoading(false);
      }
    };
    validatePermission();
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const columns = [
    {
      titleText: 'Folio',
      dataIndex: 'folio',
      key: 'folio',
      width: 200,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 400,
    },
    {
      titleText: 'Tipo de Alerta',
      dataIndex: 'tipo_de_alerta',
      key: 'tipo_de_alerta',
      width: 400,
      render: (e) => tiposDeAlertas.find((i) => i.id === e)?.descripcion,
    },
    {
      titleText: 'Fecha de Cierre',
      dataIndex: 'fecha_de_cierre',
      key: 'fecha_de_cierre',
      width: 200,
      render: (v) => (v?.format('DD/MM/YYYY')),
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 100,
      render: (e) => estadosGlobales.find((i) => i.id === e)?.descripcion,
    },
    {
      titleText: 'Usuario que canceló',
      dataIndex: 'usuario_cancelo',
      key: 'usuario_cancelo',
      width: 500,
      render: (e) => {
        const usuario = usuarios.find((u) => u.id === e);
        return usuario ? renderUser(usuario) : '';
      },
    },
    {
      titleText: 'Fecha de cancelación',
      dataIndex: 'fecha_de_cancelacion',
      key: 'fecha_de_cancelacion',
      width: 200,
      render: (v) => (v?.format('DD/MM/YYYY')),
    },
  ];

  const fetchUsuarios = async (_alertas) => {
    const promises = _alertas.filter((e) => e.usuario_cancelo)
      .map((e) => getUsuario(e.usuario_cancelo));
    const _usuarios = await Promise.all(promises);
    setUsuarios(_usuarios);
  };

  const fetchAlertas = async (pdrn) => {
    setLoading(true);
    const tipo_de_padron = tipoDePadronSelected || pdrn.tipo_de_padron;
    const dataPadron = await postConsultarCargosPorPadron(tipo_de_padron, pdrn.id);
    const padron_str = padrones.find((e) => e.id === tipo_de_padron)?.descripcion;
    const nombre_completo = padronToString(dataPadron, tipo_de_padron);
    form.setFieldsValue({ ...dataPadron, nombre_completo, padron_str });
    setPadron(dataPadron);
    await fetchUsuarios(dataPadron.alerta);
    setAlertas(dataPadron.alerta || []);
    setLoading(false);
  };

  const handleSearchPadron = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const { q } = form.getFieldsValue();
      const dataPadron = await gettersPadronesCajaMap[tipoDePadronSelected]({ q });
      const _data = dataPadron?.results || dataPadron;
      if (_data?.length === 1) {
        const [first] = dataPadron?.results || dataPadron;
        await fetchAlertas(first);
      } else if (_data?.length > 1) {
        setDataExterno(dataPadron);
        setVisibleBusquedaAvanzada(true);
        setPadron();
      } else {
        message.warn('Padrón no encontrado', 3.5);
        setPadron();
      }
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };
  const required = { required: true };

  const rules = {
    required: [required],
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
  };

  const onClear = () => {
    setDataExterno();
    setTipoDePadronSelected(1);
    form.resetFields();
    setPadron();
    setAlertas([]);
    setSelectedRowKeys([]);
  };

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
  };

  const onCancelAlerta = () => {
    formAlerta.resetFields();
    setVisibleForm(false);
    setSelectedRowKeys([]);
  };

  const FormBusqueda = () => (
    <Form
      layout="vertical"
      form={form}
      onFinish={() => handleSearchPadron()}
      scrollToFirstError
      initialValues={{ padron: 1 }}
      className={(!!padron && 'without-feedback') || ''}
    >
      <Row gutter={10}>
        {!padron ? (
          <>
            <Col
              xs={24}
              sm={24}
              md={8}
            >
              <Form.Item
                name="padron"
                label="Padrón"
                hasFeedback
                rules={rules.required}
              >
                <Select
                  onChange={(val) => {
                    setTipoDePadronSelected(val);
                  }}
                  dataSource={padrones.filter((e) => e.id !== 5)}
                />
              </Form.Item>
            </Col>
            {tipoDePadronSelected && (
              <Col
                xs={24}
                sm={24}
                md={16}
              >
                <InputSearch
                  disabled={false}
                  label={padrones
                    .find((e) => e.id === tipoDePadronSelected)?.descripcion}
                  rules={rules.required}
                  onError={onError}
                  tipoDePadron={tipoDePadronSelected}
                  callback={fetchAlertas}
                  setDataExterno={setDataExterno}
                  dataExterno={dataExterno}
                  onChange={({ target: { value } }) => form.setFieldsValue({ q: value })}
                  onPressEnter={() => handleSearchPadron()}
                  title={getHelpTexts(tipoDePadronSelected)}
                  onClickAdvanced={() => setVisibleBusquedaAvanzada(true)}
                />
              </Col>
            )}
          </>
        ) : (
          <>
            {(tipoDePadronSelected === 1) && (
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  label="Clave"
                  name="clave_ciudadana"
                >
                  <Input disabled />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} sm={24} md={16}>
              <Form.Item
                label={[4, 16, 17].includes(tipoDePadronSelected) ? 'Placa y Serie' : 'Nombre'}
                name="nombre_completo"
              >
                <Input disabled />
              </Form.Item>
            </Col>
            {([1, 2].includes(tipoDePadronSelected)) && (
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  label="Rfc"
                  name="RFC"
                >
                  <Input disabled />
                </Form.Item>
              </Col>
            )}
            {(tipoDePadronSelected === 1) && (
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  label="Curp"
                  name="CURP"
                >
                  <Input disabled />
                </Form.Item>
              </Col>
            )}
            {[1, 2].includes(tipoDePadronSelected) && (
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  label="Correo"
                  rules={rules.email}
                  name="email"
                >
                  <Input disabled />
                </Form.Item>
              </Col>
            )}
            {[1, 2].includes(tipoDePadronSelected) && (
              <LadaNumero
                rules={rules}
                form={form}
                names={{
                  lada: 'lada',
                  numero_de_telefono: 'numero_de_celular',
                }}
                breakPoints={{
                  xs: 24,
                  sm: 24,
                  md: 16,
                  lg: 8,
                  xl: 8,
                  xxl: 8,
                }}
                disabled
              />
            )}
          </>
        )}
        <Form.Item name="id" hidden>
          <Input disabled />
        </Form.Item>
      </Row>
      <Form.Item hidden>
        <Button htmlType="submit" />
      </Form.Item>
    </Form>
  );

  const onFinishAlerta = async () => {
    try {
      setLoading(true);
      await formAlerta.validateFields();
      const values = formAlerta.getFieldsValue();
      const contentTyp = contentTypePadrones
        .find((e) => e.model.toLowerCase().replace(/\s/g, '') === padron.tipo_de_padron);
      const match = tiposDeAlertas.find((e) => e.id === values.tipo_de_alerta);
      if (match?.documento) delete match.documento;
      if (match?.observaciones) delete match.observaciones;
      const allValues = {
        ...match,
        ...values,
        es_restrictiva: !match.es_informativa,
        object_id: padron?.id,
        content_type: contentTyp.id,
      };
      const created = await postGenerarAlerta(allValues, {
        clave: padron?.clave, idPadron: tipoDePadronSelected,
      });
      if (created) {
        onCancelAlerta();
        setTimeout(() => fetchAlertas(padron), 1000);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const FormAlerta = () => (
    <Form
      form={formAlerta}
      layout="vertical"
      onFinish={() => onFinishAlerta()}
      scrollToFirstError
      initialValues={{
        fecha_de_registro_de_alerta: Moment(),
      }}
    >
      <Row gutter={10}>
        <Col xs={24} sm={24} md={12} lg={8}>
          <Form.Item
            name="tipo_de_alerta"
            label="Alerta"
            hasFeedback
            rules={rules.required}
          >
            <Select
              dataSource={tiposDeAlertas
                .filter((a) => [tipoDePadronSelected, 5].includes(a.padron))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12} lg={8}>
          <Form.Item
            name="fecha_de_cierre"
            label="Fecha de Cierre"
          >
            <DatePicker format={DATE_FRONTEND_FORMAT} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12} lg={8}>
          <Form.Item
            name="fecha_de_registro_de_alerta"
            label="Fecha de Registro"
            rules={rules.required}
          >
            <DatePicker format={DATE_FRONTEND_FORMAT} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12} lg={8}>
          <Uploader
            title="Documento"
            limitMB={50}
            fullSize
            formItemName="documento"
            allowedExtensions={['pdf', 'docx', 'doc']}
            rules={rules.documento}
            allowPreview={false}
          />
        </Col>
        <Col xs={24} sm={24} md={12} lg={24}>
          <Form.Item
            name="descripcion"
            rules={rules.required}
            label="Descripción"
          >
            <Input
              autoSize={{ minRows: 3, maxRows: 3 }}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="observaciones"
            rules={rules.required}
            label="Observaciones"
          >
            <Input.TextArea
              autoSize={{ minRows: 3, maxRows: 3 }}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Form.Item
            name="observaciones_internas"
            label="Observaciones internas"
          >
            <Input.TextArea
              autoSize={{ minRows: 3, maxRows: 3 }}
              allowClear
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item hidden>
        <Button htmlType="submit" />
      </Form.Item>
    </Form>
  );

  const onClickAdd = () => {
    if (padron?.id) {
      onCancelAlerta();
      setVisibleForm(true);
    } else {
      message.warn('Se necesita buscar un padrón previamente.');
    }
  };

  const getTitle = () => {
    if (visibleForm) {
      return `${!selectedRowKeys.length ? 'Agregar ' : ''}Alerta`;
    }
    if (padron) {
      return 'Padrón';
    }
    return 'Búsqueda del Padrón';
  };

  const getControls = () => {
    if (visibleForm) {
      return (
        <FormSubmitControls
          onFinish={() => formAlerta.submit()}
          onCancel={onCancelAlerta}
        />
      );
    }
    const controls = [];
    if (visible) {
      controls.push({
        text: 'Regresar',
        icon: <ArrowLeftOutlined />,
        onClick: onCancel,
      });
    } else {
      controls.push({
        text: 'Limpiar Búsqueda',
        icon: <DeleteOutlined />,
        onClick: onClear,
      });
    }
    return (
      <FormSubmitCustomControls controls={controls} />
    );
  };

  const handleCancel = async () => {
    try {
      setLoading(true);
      await formCancel.validateFields();
      const {
        motivo_de_la_cancelacion,
        documento,
        usuario_cancelo,
        fecha_de_cancelacion,
      } = formCancel.getFieldsValue();
      const id = selectedRowKeys[0];
      const canceled = await patchAlertas(id, {
        motivo_de_la_cancelacion,
        estados_globales: 5,
        documento,
        usuario_cancelo,
        fecha_de_cancelacion,
      }, { clave: padron?.clave, idPadron: tipoDePadronSelected });
      if (canceled) {
        setVisibleCancel(false);
        formCancel.resetFields();
        setTimeout(() => fetchAlertas(padron), 1000);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Card
          className="form-container"
          title={getTitle()}
          bordered={false}
          extra={getControls()}
        >
          {(!visible && !visibleForm) && (
            <>
              <FormBusqueda />
              <br />
              <Title level={4}>
                Alertas
              </Title>
              <Table
                cols={columns}
                data={alertas}
                rowSelection={rowSelection}
                handleOnRowClick={handleOnRowClick}
                controls={{
                  onClickAdd: padron?.id && onClickAdd,
                }}
                rowClassName={(record) => {
                  let classNames = '';
                  if (record.estados_globales === 5) {
                    classNames = 'txtdec-line-throught';
                  }
                  return classNames;
                }}
                customActions={[
                  {
                    text: 'Cancelar Alerta',
                    className: 'danger',
                    disabledBySelection: true,
                    onClick: () => {
                      const [key] = selectedRowKeys;
                      const match = alertas.find((e) => e.id === key);
                      if (match?.estados_globales === 5) {
                        message.warn('La alerta ya está cancelada.');
                        return;
                      }
                      if (key) {
                        setVisibleCancel(true);
                      } else {
                        message.warn('Se necesita seleccionar una alerta.');
                      }
                    },
                    icon: <DeleteOutlined />,
                  },
                ]}
                allowSearch={false}
                rowKey="id"
                permission={permission}
                hasAllViewPermission={hasAllViewPermission}
              />
            </>
          )}
          {visibleForm && (
            <FormAlerta />
          )}
        </Card>
        <BusquedaAvanzadaPadrones
          tipoDePadron={tipoDePadronSelected}
          visible={visibleBusquedaAvanzada}
          setVisible={setVisibleBusquedaAvanzada}
          callback={fetchAlertas}
          dataExterno={dataExterno}
          setDataExterno={setDataExterno}
        />
      </Spin>
      <Modal
        visible={visibleCancel}
        closable={false}
        footer={null}
        centered
        title={(
          <FormSubmitControls
            onCancel={() => {
              setVisibleCancel(false);
              formCancel.resetFields();
            }}
            onFinish={() => handleCancel()}
            label="Cancelar Alerta"
          />
        )}
      >
        <Form
          layout="vertical"
          form={formCancel}
          onFinish={() => handleCancel()}
          scrollToFirstError
          initialValues={{
            fecha_de_cancelacion: Moment(),
            usuario_cancelo: user?.id,
          }}
        >
          <Row>
            <Col span={24}>
              <Form.Item
                label="Motivo de cancelación"
                name="motivo_de_la_cancelacion"
                rules={rules.required}
              >
                <Input.TextArea autoSize={{ minRows: 4, maxRows: 4 }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Fecha de cancelación"
                name="fecha_de_cancelacion"
              >
                <DatePicker format={DATE_FRONTEND_FORMAT} disabled />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="usuario que canceló"
                name="usuario_cancelo"
              >
                <Select
                  dataSource={[user]}
                  disabled
                  render={renderUser}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Uploader
                title="Documento"
                limitMB={50}
                fullSize
                formItemName="documento"
                allowedExtensions={['pdf', 'docx', 'doc']}
                rules={rules.documento}
                allowPreview={false}
              />
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Row>
        </Form>
      </Modal>
    </Row>
  );
};

export default Alertas;
